import CtaButtonGroup, { CtaType } from "@/components/molecules/CtaButtonGroup";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SectionHeader from "./shared/SectionHeader";
import Text from "./shared/Text";

/**
 * PricingCoreFeaturesSection — "Every plan includes core features".
 *
 * Centered title above a 3-column checklist grid, a CTA pair sitting
 * centered beneath. Green check marks, no tile backgrounds — spare and
 * scannable.
 */

export type PricingCoreFeaturesData = {
  title: string;
  features: string[];
  ctas?: CtaType[];
};

const Row = ({ text }: { text: string }) => (
  <li className="flex items-start gap-3">
    <FontAwesomeIcon
      icon={faCheck}
      aria-hidden="true"
      className="mt-1 h-3.5 w-3.5 shrink-0 text-[#2CA25F]"
    />
    <Text
      as="span"
      variant="body-md"
      className="text-[14px] leading-snug text-neutralBlack md:text-[15px]"
    >
      {text}
    </Text>
  </li>
);

const PricingCoreFeaturesSection = ({
  title,
  features,
  ctas,
}: PricingCoreFeaturesData) => {
  // Split into 3 balanced columns for a clean tabular feel.
  const cols: string[][] = [[], [], []];
  features.forEach((f, i) => cols[i % 3].push(f));

  return (
    <section className="relative w-full overflow-hidden bg-white">
      <div className="container mx-auto px-4 pb-10 pt-4 sm:px-6 md:px-8 md:pb-20 md:pt-6 lg:px-12 lg:pb-24 lg:pt-8">
        <SectionHeader title={title} align="center" variant="light-olive" />

        <div className="mx-auto mt-10 grid max-w-6xl grid-cols-1 gap-x-10 gap-y-4 sm:grid-cols-2 md:mt-14 md:grid-cols-3 md:gap-x-14 md:gap-y-5">
          {cols.map((col, idx) => (
            <ul key={idx} className="flex flex-col gap-4 md:gap-5">
              {col.map((f) => (
                <Row key={f} text={f} />
              ))}
            </ul>
          ))}
        </div>

        {ctas && ctas.length ? (
          <div className="mt-12 flex flex-wrap items-center justify-center gap-3 md:mt-16">
            <CtaButtonGroup
              ctas={ctas}
              gap="flex flex-wrap items-center gap-3"
              fullWidthOnMobile={false}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default PricingCoreFeaturesSection;
